import { useEffect, useState, useRef } from "react";
import { pb } from "@/lib/pocketbase";
import { RecordModel } from 'pocketbase';
import { useSearchParams } from "react-router-dom";

// Update Translation interface
interface Translation {
  translations: {
    np: {
      title: string;
      videoSectionTitle?: string; // Add this for product translations
    }
  }
}

// Add TranslatedVideo interface
interface TranslatedVideo extends Video {
  translations?: Translation;
}

interface Video extends RecordModel {
  title: string;
  youtube_id: string;
  order: number;
  product: string[];
  isVisible: boolean;
  translations?: Translation;
}

interface VideoPlayerProps {
  productId: string;
  videoSectionTitle?: string;
  productTranslations?: Translation; // Add this prop
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({ 
  productId, 
  videoSectionTitle,
  productTranslations 
}) => {
    const [videos, setVideos] = useState<TranslatedVideo[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchParams] = useSearchParams();
    const locale = searchParams.get('locale') || 'en';
    
    useEffect(() => {
        const loadVideos = async () => {
            try {
                const response = await pb.collection('videos').getList<Video>(1, 50, {
                    filter: `product.id ?~ "${productId}" && isVisible = true`,
                    sort: '+order'
                });

                // Get translations for each video
                const videosWithTranslations = await Promise.all(
                    response.items.map(async (video) => {
                        try {
                            const translation = await pb.collection('translations')
                                .getFirstListItem<Translation>(
                                    `collection_name = "videos" && record_id = "${video.id}"`
                                );
                            return { ...video, translations: translation } as TranslatedVideo;
                        } catch (error) {
                            return video as TranslatedVideo;
                        }
                    })
                );

                setVideos(videosWithTranslations);
            } catch (error) {
                console.error('Error loading videos:', error);
            } finally {
                setLoading(false);
            }
        };

        if (productId) {
            loadVideos();
        }
    }, [productId]);

    const getTranslatedContent = (video: TranslatedVideo) => {
        if (locale === 'en') {
            return video.title;
        }
        return video.translations?.translations?.np?.title || video.title;
    };

    const getTranslatedTitle = () => {
        if (locale === 'en') {
            return videoSectionTitle;
        }
        return productTranslations?.translations?.np?.videoSectionTitle || videoSectionTitle;
    };

    const getFontClass = () => {
        return locale === 'np' ? 'font-noto' : 'font-slate';
    };

    const formatTitle = (title: string) => {
        return title.split('\n').map((line, i) => (
            <span key={i} className={getFontClass()}>
                {line}
                {i !== title.split('\n').length - 1 && <br />
                }
            </span>
        ));
    };

    const getYouTubeEmbedUrl = (videoId: string) => {
        // Add necessary parameters to make videos playable on mobile
        return `https://www.youtube.com/embed/${videoId}?playsinline=1`;
    };

    if (loading || videos.length === 0 || !videoSectionTitle) return null;

    // Add debugging logs to help identify the issue
    console.log("VideoPlayer Debug:", {
        loading,
        videosLength: videos.length,
        videoSectionTitle,
        productId,
        productTranslations
    });

    return (
        <section className="w-full mt-12 mb-12 px-4">
            {videoSectionTitle && (
                <h2 className={`text-[rgba(0,63,40,1)] text-4xl font-bold text-center mb-[32px] whitespace-pre-line ${getFontClass()}`}>
                    {formatTitle(getTranslatedTitle())}
                </h2>
            )}
            <div className="overflow-x-auto no-scrollbar">
                <div className="flex gap-4 w-max pb-4">
                    {videos.map((video) => (
                        <div 
                            key={video.id} 
                            className="w-[320px] first:ml-0 shrink-0"
                        >
                            <div className="aspect-video w-full rounded-xl overflow-hidden bg-black">
                                <iframe
                                    className="w-full h-full"
                                    src={getYouTubeEmbedUrl(video.youtube_id)}
                                    title={getTranslatedContent(video)}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    // Fix the TypeScript error by using a JSX-appropriate attribute name with proper type casting
                                    // playsInline={true as any}
                                />
                            </div>
                            <h3 className={`mt-2 text-sm font-medium text-gray-900 ${getFontClass()}`}>
                                {getTranslatedContent(video)}
                            </h3>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};